import { useEffect, useRef, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import UploadPanel from "@/components/UploadPanel";
import { ASSETS } from "@/lib/assets";
import { Heart, Play, Timer, Upload, Users } from "lucide-react";

const HIGHLIGHT_ICONS = [Users, Upload, Timer];

function HeroVideo({ src, poster, className, label }) {
  const ref = useRef(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const reveal = () => {
      video.style.opacity = "1";
    };
    video.addEventListener("loadeddata", reveal);
    video.addEventListener("canplay", reveal);
    if (video.readyState >= 3) reveal();
    return () => {
      video.removeEventListener("loadeddata", reveal);
      video.removeEventListener("canplay", reveal);
    };
  }, [src]);

  return (
    <div className={`group relative overflow-hidden bg-zinc-900 ${className}`}>
      <video
        ref={ref}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={poster}
        className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700"
      >
        <source src={src} type="video/mp4" />
      </video>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#514bba]/55 via-transparent to-transparent opacity-80 transition-opacity group-hover:opacity-100" />
      {label ? (
        <span className="pointer-events-none absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/35 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white backdrop-blur-sm">
          <Play className="h-3 w-3 fill-white text-white" />
          {label}
        </span>
      ) : null}
    </div>
  );
}

export default function HeroSection({
  uploadState,
  uploadError,
  onFile,
  onDemo,
  onRetry,
  savedBanner,
}) {
  const { t } = useLang();
  const [rotIdx, setRotIdx] = useState(0);
  const [activeClip, setActiveClip] = useState(0);

  const clips = [
    { src: ASSETS.heroVideo, poster: ASSETS.heroPoster, label: "Familia" },
    ...ASSETS.pillarVideos.map((src, i) => ({
      src,
      poster: ASSETS.heroPoster,
      label: `Momento ${i + 1}`,
    })),
  ];

  useEffect(() => {
    const id = setInterval(
      () => setRotIdx((i) => (i + 1) % t.hero.rotators.length),
      2600
    );
    return () => clearInterval(id);
  }, [t.hero.rotators.length]);

  useEffect(() => {
    const id = setInterval(
      () => setActiveClip((i) => (i + 1) % clips.length),
      5200
    );
    return () => clearInterval(id);
  }, [clips.length]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pb-10 pt-[92px] md:pt-[100px]"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 top-20 h-72 w-72 rounded-full opacity-30 blur-3xl"
        style={{ background: "#514bbaff" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-20 top-40 h-96 w-96 rounded-full opacity-20 blur-3xl"
        style={{ background: "#514bba" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-1/2 h-64 w-[120%] -translate-x-1/2 opacity-[0.06]"
        style={{
          background:
            "radial-gradient(ellipse at center, #514bba 0%, transparent 70%)",
        }}
      />

      <div className="container-x relative">
        <AnimatePresence>{savedBanner}</AnimatePresence>

        <div
          className={`grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-12 xl:gap-16${savedBanner ? " pt-14 md:pt-16" : ""}`}
        >
          <div className="flex max-w-xl flex-col lg:max-w-none">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex w-fit items-center gap-2 rounded-full border border-[color:var(--sentinel)]/20 bg-[color:var(--sentinel-tint)] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[color:var(--sentinel)]"
              data-testid="hero-overline"
            >
              <Heart className="h-3.5 w-3.5 fill-[color:var(--sentinel)] text-[color:var(--sentinel)]" />
              {t.hero.overline}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="mt-6 text-[38px] leading-[1.04] text-zinc-900 sm:text-[48px] lg:text-[56px]"
              data-testid="hero-title"
            >
              <span className="block">{t.hero.titleA}</span>
              <span className="block text-[color:var(--sentinel)]">{t.hero.titleB}.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-5 max-w-lg text-base leading-relaxed text-zinc-600 sm:text-lg"
              data-testid="hero-subtitle"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.28 }}
              className="mt-5 inline-flex min-h-[1.5rem] items-center gap-2 text-sm text-zinc-500"
            >
              <span className="inline-block h-2 w-2 rounded-full bg-[color:var(--sentinel)]" />
              <AnimatePresence mode="wait">
                <motion.span
                  key={rotIdx}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.25 }}
                  data-testid="hero-rotator"
                >
                  {t.hero.rotators[rotIdx]}
                </motion.span>
              </AnimatePresence>
            </motion.div>

            <motion.div
              id="hero-upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.22 }}
              className="relative mt-8 w-full max-w-md"
            >
              <UploadPanel
                state={uploadState}
                errorCode={uploadError}
                onFile={onFile}
                onDemo={onDemo}
                onRetry={onRetry}
                tone="light"
              />
              <span
                className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--sentinel)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white shadow-sm"
                data-testid="hero-upload-badge"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                {t.hero.uploadHint.split("·")[0]?.trim()}
              </span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.38 }}
              className="mt-6 max-w-md text-xs leading-relaxed text-zinc-400"
              data-testid="hero-trust"
            >
              {t.hero.trust}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.12, duration: 0.55 }}
            className="relative w-full"
            data-testid="hero-video-wall"
          >
            <div className="grid grid-cols-12 gap-3 sm:gap-4">
              <div className="col-span-12 sm:col-span-8">
                <HeroVideo
                  src={clips[activeClip].src}
                  poster={clips[activeClip].poster}
                  label={clips[activeClip].label}
                  className="aspect-[4/5] rounded-[28px] shadow-[0_32px_80px_-40px_rgba(81,75,186,0.65)] sm:aspect-[5/6] md:rounded-[32px]"
                />
              </div>
              <div className="col-span-12 grid grid-cols-2 gap-3 sm:col-span-4 sm:grid-cols-1 sm:gap-4">
                {ASSETS.pillarVideos.slice(0, 2).map((src, i) => (
                  <HeroVideo
                    key={src}
                    src={src}
                    poster={ASSETS.heroPoster}
                    className="aspect-[4/3] rounded-2xl shadow-[0_20px_50px_-30px_rgba(81,75,186,0.5)] sm:aspect-square"
                    label={`${i + 1}`}
                  />
                ))}
              </div>
              <div className="col-span-12 grid grid-cols-2 gap-3 sm:gap-4">
                {ASSETS.pillarVideos.slice(2).map((src, i) => (
                  <HeroVideo
                    key={src}
                    src={src}
                    poster={ASSETS.heroPoster}
                    className="aspect-[16/10] rounded-2xl shadow-[0_20px_50px_-30px_rgba(81,75,186,0.45)]"
                    label={`${i + 3}`}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => document.getElementById("hero-upload")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                  className="flex aspect-[16/10] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[color:var(--sentinel)]/35 bg-[color:var(--sentinel-tint)] text-[color:var(--sentinel)] transition hover:border-[color:var(--sentinel)] hover:bg-[color:var(--sentinel)]/10"
                  data-testid="hero-video-cta"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[color:var(--sentinel)] text-white shadow-lg">
                    <Upload className="h-5 w-5" />
                  </span>
                  <span className="text-xs font-semibold">{t.hero.primaryCta}</span>
                </button>
              </div>
            </div>

            <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
              {clips.map((clip, i) => (
                <button
                  key={clip.src}
                  type="button"
                  onClick={() => setActiveClip(i)}
                  className={`relative h-14 w-24 shrink-0 overflow-hidden rounded-xl border-2 bg-zinc-900 transition ${
                    activeClip === i
                      ? "border-[color:var(--sentinel)] ring-2 ring-[color:var(--sentinel)]/25"
                      : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`hero-video-thumb-${i}`}
                >
                  <img
                    src={clip.poster}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute inset-0 bg-[color:var(--sentinel)]/20" />
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="relative z-10 mx-auto mt-10 max-w-4xl rounded-2xl border border-[color:var(--sentinel)]/15 bg-white shadow-[0_24px_60px_-40px_rgba(81,75,186,0.35)]"
        >
          <div className="grid grid-cols-1 divide-y divide-zinc-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            {t.hero.highlights.map((item, index) => {
              const Icon = HIGHLIGHT_ICONS[index] || Upload;
              return (
                <div
                  key={item.title}
                  className="flex flex-col items-center gap-1 px-5 py-4 text-center"
                  data-testid={`hero-highlight-${index}`}
                >
                  <Icon className="h-4 w-4 text-[color:var(--sentinel)]" />
                  <p className="text-sm font-semibold text-zinc-900">{item.title}</p>
                  <p className="text-xs text-zinc-500">{item.subtitle}</p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
